/**
 * @fileoverview Main Operator Administration Panel for Adivina Adivinador (Riddle - Tile 10).
 * Features:
 * - Projection cards (Show Cover, Project Game Arena, QR Instructions, Theme Customization, Trivia Package Management).
 * - Mandatory trivia selector with preventive validation block if empty or questionless.
 * - Live Screen Layout Selector (1: Clásico, 2: Invertido, 3: Solo Imagen, 4: Solo Opciones).
 * - Live ShowCorrectAnswer switch toggle.
 * - Live Game Navigation & Lifecycle Controls (Start Game, Reveal Silhouette, Next/Previous, Send Results, Reset).
 * - Results charts and zone telemetry breakdown.
 * - Real-time WebSocket synchronization.
 * 
 * @module components/Admin/Games/Riddle
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { SecondaryBase } from './../Commons/SecondaryBase';
import { Confirm } from './../../Commons/Modals/Confirm';
import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage';
import { ResetCurrentDynamic as BLResetCurrentDynamic } from './../../../utilities/BL/Zones';

// Subcomponents / Modals
import { RiddleTriviaModal } from './Riddle/RiddleTriviaModal';
import { RiddleThemeModal } from './Riddle/RiddleThemeModal';
import { RiddleQrModal } from './Riddle/RiddleQrModal';

// Business Logic
import {
    getTriviaSets,
    getTriviaSetById,
    getSettings,
    updateSettings,
    showCover,
    showGame,
    sendQuestion,
    displayItem,
    changeLayout,
    sendResults,
    resetResults,
    getResults
} from './../../../utilities/BL/Admin/Games/Riddle';

// WebSocket Client
import {
    ConnectWebSocketForRiddleAdmin,
    DisconnectWebSocketForRiddleAdmin
} from './../../../utilities/WS/Admin/RiddleWS';

const barColors = ['#f44336', '#2196f3', '#4caf50', '#ff9800', '#9c27b0', '#00bcd4', '#e91e63', '#795548'];

/**
 * Resolves a media path into an absolute preview URL.
 * 
 * @param {string} mediaPath - Relative or absolute image path.
 * @returns {string} Absolute preview URL.
 */
const resolveMediaUrl = (mediaPath) => {
    if (!mediaPath) return '';
    if (mediaPath.startsWith('http://') || mediaPath.startsWith('https://') || mediaPath.startsWith('blob:')) {
        return mediaPath;
    }
    const apiBaseUrl = (process.env.REACT_APP_API_URL || '').replace('/api/', '/').replace('/api', '/');
    return `${apiBaseUrl}${mediaPath.startsWith('/') ? mediaPath.substring(1) : mediaPath}`;
};

/**
 * General Results Bar Chart Component.
 */
const GeneralResultsChart = ({ data }) => {
    return (
        <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data}>
                <XAxis dataKey="text" stroke="#666" />
                <YAxis stroke="#666" />
                <Tooltip />
                <Bar dataKey="points" fill="#8884d8">
                    {data.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={barColors[index % barColors.length]} />
                    ))}
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
};

/**
 * Question Results Bar Chart Component.
 */
const BarResultsByQuestion = ({ questionId, pointsByZone, results }) => {
    const data = results
        .map(zone => {
            const key = zone.key;
            const points = pointsByZone[key];
            return points ? { name: zone.text, points } : null;
        })
        .filter(Boolean);

    return (
        <div className="mb-5 pb-4 border rounded-3 p-3 bg-light">
            <p className="fw-bold text-center fs-5 text-dark">Adivinanza #{questionId}</p>

            <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data}>
                    <XAxis dataKey="name" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip />
                    <Bar dataKey="points">
                        {data.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={barColors[index % barColors.length]} />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>

            <div className="row mt-3">
                {data.map((zone, index) => (
                    <div key={index} className="col-md-4 col-6 small fw-semibold text-secondary">
                        <i className="bi bi-geo-alt-fill text-danger me-1"></i>
                        {zone.name}: <span className="text-dark fw-bold">{zone.points} pts</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

/**
 * Utility to render Key-Value dictionary lists sorted alphabetically.
 */
const KeyValueList = ({ data }) => {
    if (!data || typeof data !== 'object' || Object.keys(data).length === 0) {
        return <p className="small text-muted mb-0">0 respuestas registradas</p>;
    }

    const entries = Object.entries(data).sort((a, b) =>
        a[0].localeCompare(b[0], 'es', { sensitivity: 'base' })
    );

    return (
        <ul className="list-unstyled small mb-0">
            {entries.map(([zone, value]) => (
                <li key={zone} className="text-secondary">
                    <span className="fw-semibold text-dark">{zone}:</span> {value}
                </li>
            ))}
        </ul>
    );
};

/**
 * Screen Layout Modes.
 */
const SCREEN_LAYOUTS = [
    { id: 1, title: 'Clásico', subtitle: 'Silueta Izq / Opciones Der', icon: 'bi-layout-split' },
    { id: 2, title: 'Invertido', subtitle: 'Silueta Der / Opciones Izq', icon: 'bi-layout-sidebar-reverse' },
    { id: 3, title: 'Solo Imagen', subtitle: 'Silueta Centrada', icon: 'bi-image' },
    { id: 4, title: 'Solo Opciones', subtitle: 'Pantalla Completa', icon: 'bi-card-list' }
];

/**
 * Main Riddle Admin Console Component.
 * 
 * @function Riddle
 * @param {Object} props
 * @param {Function} props.getOutOfTheDynamic - Callback to exit back to admin dashboard.
 * @returns {React.ReactElement}
 */
export const Riddle = ({ getOutOfTheDynamic }) => {
    const { showMessage } = useFloatMessage();

    // Trivia Sets & Question Management
    const [triviaSetsList, setTriviaSetsList] = useState([]);
    const [selectedTriviaSetId, setSelectedTriviaSetId] = useState('');
    const [activeTriviaSetDetail, setActiveTriviaSetDetail] = useState(null);
    const [quizz, setQuizz] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);

    // Operational Game State
    const [gameStarted, setGameStarted] = useState(false);
    const [isItemVisible, setIsItemVisible] = useState(false);
    const [currentScreenLayout, setCurrentScreenLayout] = useState(1);
    const [showCorrectAnswer, setShowCorrectAnswer] = useState(true);
    const [isCoverProjected, setIsCoverProjected] = useState(false);
    const [isGameProjected, setIsGameProjected] = useState(false);
    const [isOperationInProgress, setIsOperationInProgress] = useState(false);

    // Results & Telemetry
    const [showResults, setShowResults] = useState(false);
    const [results, setResults] = useState(null);
    const [questionsResults, setQuestionsResults] = useState(null);
    const [detailedResults, setDetailedResults] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);

    // Modals
    const [isTriviaModalOpen, setIsTriviaModalOpen] = useState(false);
    const [isThemeModalOpen, setIsThemeModalOpen] = useState(false);
    const [isQrModalOpen, setIsQrModalOpen] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState({
        message: '',
        actionToYesButton: null,
        textYesButton: 'Sí, confirmar',
        textNoButton: 'Cancelar'
    });

    /**
     * Handles real-time events received from WebSocket.
     */
    const handleWebSocketMessageReceived = useCallback((packet) => {
        if (!packet || typeof packet !== 'object') return;

        switch (packet.type) {
            case 'riddle_state':
            case 'riddle_snapshot':
                if (packet.data) {
                    if (packet.data.screenLayout) setCurrentScreenLayout(packet.data.screenLayout);
                    if (packet.data.showCorrectAnswer !== undefined) setShowCorrectAnswer(packet.data.showCorrectAnswer);
                }
                break;
            case 'riddle_cover_shown':
                setIsCoverProjected(true);
                setIsGameProjected(false);
                break;
            case 'riddle_game_shown':
                setIsGameProjected(true);
                setIsCoverProjected(false);
                break;
            case 'riddle_question_sent':
                setGameStarted(true);
                setIsItemVisible(false);
                break;
            case 'riddle_item_displayed':
                setIsItemVisible(true);
                break;
            case 'riddle_layout_changed':
                if (packet.screenLayout) setCurrentScreenLayout(packet.screenLayout);
                break;
            case 'riddle_results_reset':
                setGameStarted(false);
                setIsItemVisible(false);
                setShowResults(false);
                break;
            default:
                break;
        }
    }, []);

    /**
     * Initializes component data and WebSocket connection.
     */
    useEffect(() => {
        loadInitialData();

        ConnectWebSocketForRiddleAdmin(handleWebSocketMessageReceived);

        return () => {
            DisconnectWebSocketForRiddleAdmin();
        };
    }, [handleWebSocketMessageReceived]);

    /**
     * Loads trivia sets and general settings.
     */
    const loadInitialData = async () => {
        try {
            document.getElementById('root')?.classList.add('BodyComplete');

            // 1. Fetch settings
            const settingsResponse = await getSettings();
            if (settingsResponse.success && settingsResponse.data) {
                const s = settingsResponse.data;
                if (s.screenLayout) setCurrentScreenLayout(s.screenLayout);
                if (s.showCorrectAnswer !== undefined) setShowCorrectAnswer(s.showCorrectAnswer);
                if (s.activeTriviaSetId) setSelectedTriviaSetId(s.activeTriviaSetId);
            }

            // 2. Fetch trivia sets
            const setsResponse = await getTriviaSets();
            if (setsResponse.success && Array.isArray(setsResponse.data)) {
                setTriviaSetsList(setsResponse.data);

                // Auto-select active or first trivia set if available
                let targetTriviaId = settingsResponse.data?.activeTriviaSetId;
                if (!targetTriviaId && setsResponse.data.length > 0) {
                    targetTriviaId = setsResponse.data[0].id;
                }

                if (targetTriviaId) {
                    setSelectedTriviaSetId(targetTriviaId);
                    await loadTriviaDetail(targetTriviaId);
                }
            }
        } catch (error) {
            console.error('Error loading initial data in Riddle:', error);
        }
    };

    /**
     * Loads questions and full detail for a selected trivia set.
     */
    const loadTriviaDetail = async (triviaId) => {
        if (!triviaId) {
            setActiveTriviaSetDetail(null);
            setQuizz([]);
            setCurrentIndex(0);
            return;
        }

        try {
            const detailResponse = await getTriviaSetById(triviaId);
            if (detailResponse.success && detailResponse.data) {
                setActiveTriviaSetDetail(detailResponse.data);
                const questionsList = detailResponse.data.questions || [];
                setQuizz(questionsList);
                setCurrentIndex(0);
            } else {
                setActiveTriviaSetDetail(null);
                setQuizz([]);
                setCurrentIndex(0);
            }
        } catch (error) {
            console.error('Error loading trivia detail:', error);
        }
    };

    /**
     * Handler for trivia selection change from dropdown.
     */
    const handleTriviaSetSelectionChange = async (newTriviaId) => {
        setSelectedTriviaSetId(newTriviaId);
        setGameStarted(false);
        setIsItemVisible(false);
        setShowResults(false);

        if (newTriviaId) {
            await loadTriviaDetail(newTriviaId);
            // Update active trivia in settings
            try {
                const currentSettings = await getSettings();
                if (currentSettings.success && currentSettings.data) {
                    await updateSettings({
                        ...currentSettings.data,
                        activeTriviaSetId: newTriviaId
                    });
                }
            } catch {
                // Ignore silent update
            }
        } else {
            setActiveTriviaSetDetail(null);
            setQuizz([]);
            setCurrentIndex(0);
        }
    };

    /**
     * Returns to main dashboard after resetting current dynamic.
     */
    const ReturnToDashboard = async () => {
        try {
            await BLResetCurrentDynamic();
            getOutOfTheDynamic();
        } catch (error) {
            getOutOfTheDynamic();
        }
    };

    // =========================================================================
    // PROJECTION ACTIONS
    // =========================================================================

    /**
     * Projects Cover Screen.
     */
    const handleShowCover = async () => {
        setIsOperationInProgress(true);
        try {
            const response = await showCover();
            if (response.success) {
                setIsCoverProjected(true);
                setIsGameProjected(false);
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: 'Portada de Adivina Adivinador proyectada en pantalla.'
                });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: response.message || 'Error al proyectar la portada.'
                });
            }
        } catch (error) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al proyectar portada.'
            });
        } finally {
            setIsOperationInProgress(false);
        }
    };

    /**
     * Projects Game Arena Screen.
     */
    const handleShowGame = async () => {
        setIsOperationInProgress(true);
        try {
            const response = await showGame();
            if (response.success) {
                setIsGameProjected(true);
                setIsCoverProjected(false);
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: 'Escenario de Adivina Adivinador proyectado en pantalla.'
                });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: response.message || 'Error al proyectar el escenario.'
                });
            }
        } catch (error) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al proyectar escenario.'
            });
        } finally {
            setIsOperationInProgress(false);
        }
    };

    /**
     * Changes Screen Layout in real time.
     */
    const handleSelectLayout = async (layoutId) => {
        setCurrentScreenLayout(layoutId);
        try {
            const response = await changeLayout(layoutId);
            if (response.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: `Layout de pantalla cambiado a modo ${layoutId}.`
                });
            }
        } catch (error) {
            console.error('Error changing layout:', error);
        }
    };

    /**
     * Toggles ShowCorrectAnswer switch.
     */
    const handleToggleShowCorrectAnswer = async (newVal) => {
        setShowCorrectAnswer(newVal);
        try {
            const settingsResponse = await getSettings();
            if (settingsResponse.success && settingsResponse.data) {
                await updateSettings({
                    ...settingsResponse.data,
                    showCorrectAnswer: newVal
                });
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: newVal ? 'Mostrar respuesta correcta habilitado.' : 'Mostrar respuesta correcta deshabilitado.'
                });
            }
        } catch (error) {
            console.error('Error updating showCorrectAnswer:', error);
        }
    };

    // =========================================================================
    // GAMEPLAY & QUESTION NAVIGATION
    // =========================================================================

    /**
     * Sends a question to Screen and Mobile Fans.
     */
    const SendRiddleQuestionToUsers = async (question, questionNumber = 1) => {
        try {
            const response = await sendQuestion(question, currentScreenLayout, showCorrectAnswer, questionNumber);
            if (response.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: `Adivinanza #${questionNumber} enviada a la Afición y a la Pantalla.`
                });
                return { success: true };
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: response.message || 'Error al enviar la adivinanza.'
                });
                return { success: false, message: response.message };
            }
        } catch (error) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al enviar adivinanza.'
            });
            return { success: false };
        }
    };

    /**
     * Starts the game with the first question.
     */
    const StartGame = async () => {
        if (!quizz || quizz.length === 0) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'No hay preguntas disponibles en el paquete seleccionado.'
            });
            return;
        }

        setIsOperationInProgress(true);
        try {
            setIsItemVisible(false);
            setCurrentIndex(0);
            const firstQuestion = quizz[0];

            const response = await SendRiddleQuestionToUsers(firstQuestion, 1);
            if (response.success) {
                setGameStarted(true);
                setShowResults(false);
            }
        } finally {
            setIsOperationInProgress(false);
        }
    };

    /**
     * Advances to the next question.
     */
    const handleNext = async () => {
        if (currentIndex < quizz.length - 1) {
            const nextIndex = currentIndex + 1;
            const nextQuestion = quizz[nextIndex];
            setCurrentIndex(nextIndex);
            setIsItemVisible(false);

            if (gameStarted) {
                await SendRiddleQuestionToUsers(nextQuestion, nextIndex + 1);
            }
        }
    };

    /**
     * Returns to the previous question.
     */
    const handlePrev = async () => {
        if (currentIndex > 0) {
            const prevIndex = currentIndex - 1;
            const prevQuestion = quizz[prevIndex];
            setCurrentIndex(prevIndex);
            setIsItemVisible(false);

            if (gameStarted) {
                await SendRiddleQuestionToUsers(prevQuestion, prevIndex + 1);
            }
        }
    };

    /**
     * Reveals hidden silhouette and optionally highlights correct answer.
     */
    const handleShowItemOnScreen = async (currentQuestion) => {
        setIsOperationInProgress(true);
        try {
            const response = await displayItem(currentQuestion, currentIndex + 1);
            if (response.success) {
                setIsItemVisible(true);
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: '¡Elemento revelado exitosamente en pantalla y móviles!'
                });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: response.message || 'Error al revelar el elemento.'
                });
            }
        } catch (error) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al revelar elemento.'
            });
        } finally {
            setIsOperationInProgress(false);
        }
    };

    // =========================================================================
    // RESULTS & TELEMETRY
    // =========================================================================

    /**
     * Displays results view and projects podium on /screen.
     */
    const ShowResultsView = async () => {
        setIsOperationInProgress(true);
        try {
            setErrorMessage(null);
            const response = await getResults();

            if (!response.success) {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: 'No fue posible obtener los resultados. Inténtalo nuevamente.'
                });
                setErrorMessage('No fue posible obtener los resultados de las adivinanzas.');
                return;
            }

            const resultsByQuestion = response.data || [];
            if (resultsByQuestion.length === 0) {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: 'Aún no hay resultados registrados para esta dinámica.'
                });
                setErrorMessage('Aún no hay resultados registrados de la afición.');
                return;
            }

            setDetailedResults(resultsByQuestion);

            // Zones template
            const zonesTemplate = [
                { name: 'Zona Laboratorio Tequis', text: 'Laboratorio Tequis', points: 0, show: false },
                { name: 'Zona Canels', text: 'Canels', points: 0, show: false },
                { name: 'Zona Seguros El Potosí', text: 'Seguros El Potosí', points: 0, show: false },
                { name: 'Zona Red Cola', text: 'Red Cola', points: 0, show: false },
                { name: 'Zona Tecate', text: 'Tecate', points: 0, show: false },
                { name: 'Zona Moctezuma', text: 'Moctezuma', points: 0, show: false },
                { name: 'Zona Nissan', text: 'Nissan', points: 0, show: false },
                { name: 'Zona Mobil', text: 'Mobil', points: 0, show: false },
                { name: 'Zona Lounge Ron Potosí', text: 'Lounge Ron Potosí', points: 0, show: false },
                { name: 'Zona Azul', text: 'Azul', points: 0, show: false },
                { name: 'Zona Oro', text: 'Oro', points: 0, show: false },
                { name: 'Team Lucho', text: 'Lucho', points: 0, show: false },
                { name: 'Team Tunita', text: 'Tunita', points: 0, show: false }
            ];

            const totalPointsByZone = {};
            zonesTemplate.forEach(zone => {
                totalPointsByZone[zone.text.toLowerCase()] = 0;
            });

            const questionsBreakdown = resultsByQuestion.map(result => {
                const questionId = result.id;
                const pointsPerZone = result.calculatedPointsByZone || {};
                const normalizedPerQuestion = {};

                Object.entries(pointsPerZone).forEach(([zoneName, pts]) => {
                    const key = zoneName.toLowerCase().trim();
                    if (totalPointsByZone.hasOwnProperty(key)) {
                        totalPointsByZone[key] += pts;
                    } else {
                        totalPointsByZone[key] = pts;
                    }
                    normalizedPerQuestion[key] = pts;
                });

                return {
                    questionId,
                    pointsByZone: normalizedPerQuestion
                };
            });

            const finalResultsPerZone = zonesTemplate.map(zone => {
                const key = zone.text.toLowerCase();
                const points = totalPointsByZone[key] || 0;
                return {
                    ...zone,
                    key,
                    points,
                    show: points > 0
                };
            });

            setResults(finalResultsPerZone);
            setQuestionsResults(questionsBreakdown);
            setShowResults(true);

            // Emit to screen
            const screenRes = await sendResults();
            if (screenRes.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: 'Resultados y podio enviados a la pantalla exitosamente.'
                });
            }
        } catch (error) {
            setErrorMessage(`Error al mostrar resultados: ${error.message || error}`);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error al procesar resultados.'
            });
        } finally {
            setIsOperationInProgress(false);
        }
    };

    /**
     * Prompts confirmation to reset game results.
     */
    const handlePromptResetResults = () => {
        setConfirmConfig({
            message: '¿Estás seguro de reiniciar el juego y todas las estadísticas de Adivina Adivinador? Esta acción no se puede deshacer.',
            actionToYesButton: async () => {
                const response = await resetResults();
                if (response.success) {
                    setGameStarted(false);
                    setIsItemVisible(false);
                    setShowResults(false);
                    setCurrentIndex(0);
                    showMessage({
                        type: MESSAGE_TYPE.SUCCESS,
                        text: 'Juego y estadísticas reiniciados correctamente.'
                    });
                    return { success: true };
                } else {
                    return { success: false, message: response.message || 'Error al reiniciar.' };
                }
            },
            textYesButton: 'Sí, reiniciar',
            textNoButton: 'Cancelar'
        });
        setIsConfirmModalOpen(true);
    };

    // Sort detailed results ascending
    const Sorted = useMemo(() => {
        if (!Array.isArray(detailedResults)) return [];
        return [...detailedResults].sort((a, b) => {
            const idA = typeof a?.id === 'number' ? a.id : Number(a?.id) || 0;
            const idB = typeof b?.id === 'number' ? b.id : Number(b?.id) || 0;
            return idA - idB;
        });
    }, [detailedResults]);

    const isTriviaValid = selectedTriviaSetId && quizz.length > 0;
    const currentQuestion = isTriviaValid ? quizz[currentIndex] : null;

    return (
        <SecondaryBase actionToExecute={ReturnToDashboard}>
            <section className="Riddle AdminConsole">
                {/* HEADER TITLE & STATUS BAR */}
                <div className="text-center mb-4">
                    <h2 className="Riddle__Title fw-bold text-dark d-flex align-items-center justify-content-center">
                        <i className="bi bi-question-square-fill text-danger me-2"></i>
                        Adivina Adivinador (Riddle - Tile 10)
                    </h2>
                    <p className="text-muted small">
                        Consola del Operador de Estadio • Proyección Dinámica, Revelación SkiaSharp y Concurrencia Masiva
                    </p>
                </div>

                {/* 1. TARJETA DE PROYECCIÓN INDEPENDIENTE Y ACCIONES SUPERIORES */}
                <div className="Riddle__ProjectionCard card border-0 shadow-sm rounded-4 p-3 mb-4 bg-white">
                    <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center">
                        <div className="d-flex flex-wrap gap-2">
                            <button
                                type="button"
                                className={`btn btn-sm fw-bold ${isCoverProjected ? 'btn-success' : 'btn-outline-danger'}`}
                                onClick={handleShowCover}
                                disabled={isOperationInProgress}
                            >
                                <i className="bi bi-display me-1"></i>
                                {isCoverProjected ? 'Portada Proyectada' : 'Mostrar Portada'}
                            </button>

                            <button
                                type="button"
                                className={`btn btn-sm fw-bold ${isGameProjected ? 'btn-success' : 'btn-outline-primary'}`}
                                onClick={handleShowGame}
                                disabled={isOperationInProgress}
                            >
                                <i className="bi bi-controller me-1"></i>
                                {isGameProjected ? 'Escenario Proyectado' : 'Proyectar Adivinanza'}
                            </button>

                            <button
                                type="button"
                                className="btn btn-sm btn-outline-dark fw-bold"
                                onClick={() => setIsQrModalOpen(true)}
                            >
                                <i className="bi bi-qr-code-scan me-1"></i> Ver Instrucciones QR
                            </button>
                        </div>

                        <div className="d-flex flex-wrap gap-2">
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary fw-bold"
                                onClick={() => setIsThemeModalOpen(true)}
                            >
                                <i className="bi bi-palette me-1"></i> Personalizar Temas
                            </button>

                            <button
                                type="button"
                                className="btn btn-sm btn-primary fw-bold"
                                onClick={() => setIsTriviaModalOpen(true)}
                            >
                                <i className="bi bi-collection-play me-1"></i> Gestionar Trivias
                            </button>
                        </div>
                    </div>
                </div>

                {/* 2. SELECTOR OBLIGATORIO DE TRIVIA Y CONFIGURACIÓN EN VIVO */}
                <div className="card border-0 shadow-sm rounded-4 p-4 mb-4 bg-white">
                    <div className="row g-3 align-items-center">
                        {/* Mandatory Trivia Selector */}
                        <div className="col-lg-6 col-md-12">
                            <label className="form-label fw-bold text-dark small mb-1">
                                <i className="bi bi-check2-square text-danger me-1"></i>
                                Paquete de Trivia Activo: *
                            </label>
                            <select
                                className={`form-select form-select-lg ${!selectedTriviaSetId ? 'border-danger bg-danger-subtle' : 'border-primary'}`}
                                value={selectedTriviaSetId}
                                onChange={(e) => handleTriviaSetSelectionChange(e.target.value)}
                            >
                                <option value="">Selecciona una opción</option>
                                {triviaSetsList.map((t) => (
                                    <option key={t.id} value={t.id}>
                                        {t.title} ({t.questionsCount || 0} adivinanzas) {t.isActive ? '' : '(Inactivo)'}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* ShowCorrectAnswer Switch */}
                        <div className="col-lg-6 col-md-12 d-flex justify-content-lg-end align-items-center pt-lg-3">
                            <div className="form-check form-switch bg-light p-2 px-3 rounded-3 border d-flex align-items-center gap-3">
                                <label className="form-check-label fw-bold text-dark small mb-0" htmlFor="adminShowCorrectAnswerSwitch">
                                    <i className="bi bi-eye-fill text-primary me-1"></i>
                                    Mostrar Respuesta Correcta al Revelar
                                </label>
                                <input
                                    type="checkbox"
                                    className="form-check-input fs-5 m-0"
                                    id="adminShowCorrectAnswerSwitch"
                                    checked={showCorrectAnswer}
                                    onChange={(e) => handleToggleShowCorrectAnswer(e.target.checked)}
                                />
                            </div>
                        </div>
                    </div>

                    {/* 3. SELECTOR EN VIVO DE LOS 4 LAYOUTS DE PANTALLA */}
                    <div className="mt-4 pt-3 border-top">
                        <label className="form-label fw-bold text-dark small mb-2 d-block">
                            <i className="bi bi-aspect-ratio text-primary me-1"></i>
                            Distribución de Pantalla en Vivo (Screen Layout):
                        </label>
                        <div className="row g-2">
                            {SCREEN_LAYOUTS.map((layout) => (
                                <div key={layout.id} className="col-md-3 col-6">
                                    <button
                                        type="button"
                                        className={`btn w-100 p-2 text-start rounded-3 border transition ${
                                            currentScreenLayout === layout.id
                                                ? 'btn-primary shadow-sm text-white'
                                                : 'btn-outline-secondary bg-light'
                                        }`}
                                        onClick={() => handleSelectLayout(layout.id)}
                                    >
                                        <div className="d-flex align-items-center">
                                            <i className={`bi ${layout.icon} fs-4 me-2`}></i>
                                            <div>
                                                <div className="fw-bold small">{layout.id}: {layout.title}</div>
                                                <div className="extra-small opacity-75">{layout.subtitle}</div>
                                            </div>
                                        </div>
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* PREVENTIVE VALIDATION BLOCK BANNER */}
                {!isTriviaValid && (
                    <div className="alert alert-warning border-warning shadow-sm rounded-4 p-4 text-center my-4">
                        <i className="bi bi-exclamation-triangle-fill fs-2 text-warning d-block mb-2"></i>
                        <h5 className="fw-bold text-dark mb-1">Debes seleccionar un paquete de trivias</h5>
                        <p className="small text-secondary mb-3">
                            Selecciona un paquete de trivias con preguntas configuradas desde el selector superior o haz clic en "Gestionar Trivias" para crear o agregar preguntas.
                        </p>
                        <button
                            type="button"
                            className="btn btn-primary btn-sm fw-bold px-4"
                            onClick={() => setIsTriviaModalOpen(true)}
                        >
                            <i className="bi bi-plus-circle me-1"></i> Gestionar / Crear Trivias
                        </button>
                    </div>
                )}

                {/* MAIN GAME VIEW & CONTROLS (ONLY WHEN VALID TRIVIA SELECTED) */}
                {isTriviaValid && (
                    <>
                        {showResults ? (
                            /* RESULTS & STATISTICS VIEW */
                            <div className="card border-0 shadow-sm rounded-4 p-4 bg-white mb-4">
                                <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                                    <h4 className="fw-bold mb-0 text-dark">
                                        <i className="bi bi-bar-chart-fill text-danger me-2"></i>
                                        Resultados y Podio de Zonas
                                    </h4>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => setShowResults(false)}
                                    >
                                        <i className="bi bi-arrow-left me-1"></i> Volver al Juego
                                    </button>
                                </div>

                                <p className="fw-bold text-center fs-5 text-dark mb-3">Resultados Generales Acumulados</p>
                                {results && <GeneralResultsChart data={results.filter(r => r.show)} />}

                                <div className="row my-4 g-2">
                                    {results && results.map((result, index) => (
                                        result.show && (
                                            <div key={index} className="col-md-3 col-6">
                                                <div className="p-2 bg-light rounded-3 border text-center">
                                                    <span className="small fw-bold text-secondary d-block">{result.text}</span>
                                                    <span className="fs-5 fw-bold text-danger">{result.points} pts</span>
                                                </div>
                                            </div>
                                        )
                                    ))}
                                </div>

                                <h5 className="mt-4 pt-3 mb-3 fw-bold text-dark border-top text-center">
                                    Desglose de Resultados por Adivinanza
                                </h5>
                                {questionsResults && questionsResults.map((qr, idx) => (
                                    <BarResultsByQuestion
                                        key={idx}
                                        questionId={qr.questionId}
                                        pointsByZone={qr.pointsByZone}
                                        results={results}
                                    />
                                ))}

                                <div className="Riddle__Results__Statistics mt-4 pt-3 border-top">
                                    <p className="Riddle__Results__Statistics__Title fw-bold text-dark mb-3">
                                        Telemetría y Conectividad por Zona:
                                    </p>
                                    <div className="row g-3">
                                        {Sorted.map((q) => (
                                            <div key={q.id} className="col-md-6 col-lg-4">
                                                <div className="card h-100 p-3 bg-light border rounded-3 shadow-sm">
                                                    <p className="fw-bold text-primary mb-2">Adivinanza #{q.id}</p>
                                                    <div className="mb-2">
                                                        <span className="small fw-semibold text-secondary d-block">Conectados al iniciar:</span>
                                                        <KeyValueList data={q.connectedUsersByZoneAtStart} />
                                                    </div>
                                                    <div className="mb-2">
                                                        <span className="small fw-semibold text-secondary d-block">Puntos obtenidos:</span>
                                                        <KeyValueList data={q.calculatedPointsByZone} />
                                                    </div>
                                                    <div>
                                                        <span className="small fw-semibold text-secondary d-block">Respuestas correctas:</span>
                                                        <KeyValueList data={q.correctAnswersByZone} />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="text-center mt-5 pt-3 border-top">
                                    <button
                                        type="button"
                                        className="btn btn-outline-danger px-4 fw-bold me-2"
                                        onClick={handlePromptResetResults}
                                    >
                                        <i className="bi bi-arrow-counterclockwise me-1"></i> Reiniciar Dinámica
                                    </button>
                                </div>
                            </div>
                        ) : (
                            /* LIVE GAME PLAY CARD */
                            <>
                                <div className="card border-0 shadow-sm rounded-4 p-4 bg-white mb-4">
                                    <div className="text-center mb-3">
                                        <span className="badge bg-danger-subtle text-danger border border-danger px-3 py-2 fs-6 rounded-pill">
                                            {gameStarted ? 'Juego en Proceso en Pantalla y Móviles' : 'Adivinanza Lista para Iniciar'}
                                        </span>
                                    </div>

                                    {/* Quiz interactive container */}
                                    <div className="Riddle__Quiz">
                                        {/* Desktop Left Button */}
                                        <div className="Riddle__Quiz__LeftSection">
                                            {currentIndex > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={handlePrev}
                                                    className="Btn__Adsl--RedOutlineTransparent Riddle__Quiz__LeftSection__Button"
                                                    disabled={isOperationInProgress}
                                                >
                                                    <i className="bi bi-caret-left-fill"></i> Anterior
                                                </button>
                                            )}
                                        </div>

                                        {/* Questions and options preview */}
                                        <div className="Riddle__Quiz__Questions text-center mx-auto">
                                            <p className="fw-bold fs-5 text-dark mb-1">
                                                Adivinanza {currentIndex + 1} de {quizz.length}
                                            </p>
                                            <p className="text-secondary small mb-3">
                                                Frase: <strong className="text-dark">{currentQuestion?.instruction}</strong>
                                            </p>

                                            {/* Silhouette / Revealed Image Preview */}
                                            <div className="Riddle__Quiz__Questions__Options__ImageContainer mx-auto mb-3 position-relative">
                                                <img
                                                    src={
                                                        isItemVisible && currentQuestion?.responseImageUrl
                                                            ? resolveMediaUrl(currentQuestion.responseImageUrl)
                                                            : (isItemVisible && currentQuestion?.response?.image
                                                                ? resolveMediaUrl(currentQuestion.response.image)
                                                                : resolveMediaUrl(currentQuestion?.imageUrl || currentQuestion?.image))
                                                    }
                                                    alt="Adivinanza"
                                                    className="Riddle__Quiz__Questions__Options__ImageContainer__Image"
                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                />
                                                <span className={`badge position-absolute bottom-0 start-50 translate-middle-x mb-2 ${isItemVisible ? 'bg-success' : 'bg-dark'}`}>
                                                    {isItemVisible ? 'Revelada' : 'Silueta Oculta'}
                                                </span>
                                            </div>

                                            {/* Options List Preview */}
                                            <p className="fw-bold small text-secondary mb-2">Opciones de Respuesta</p>
                                            <div className="d-flex flex-column gap-2">
                                                {(currentQuestion?.options || []).map((option, index) => (
                                                    <div
                                                        key={index}
                                                        className={`p-2 rounded-3 text-center fw-bold border transition ${
                                                            option.isCorrect
                                                                ? 'Btn__Adsl--Success border-success'
                                                                : 'Btn__Adsl--RedOutlineTransparent'
                                                        }`}
                                                    >
                                                        <span className="me-2">{String.fromCharCode(65 + index)})</span>
                                                        {option.text}
                                                        {option.isCorrect && (
                                                            <i className="bi bi-check-circle-fill ms-2"></i>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Desktop Right Button */}
                                        <div className="Riddle__Quiz__RightSection">
                                            {isItemVisible ? (
                                                currentIndex < quizz.length - 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={handleNext}
                                                        className="Btn__Adsl--RedOutlineTransparent Riddle__Quiz__RightSection__Button"
                                                        disabled={isOperationInProgress}
                                                    >
                                                        Siguiente <i className="bi bi-caret-right-fill"></i>
                                                    </button>
                                                )
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => handleShowItemOnScreen(currentQuestion)}
                                                    className="Btn__Adsl--RedOutlineTransparent Riddle__Quiz__RightSection__Button"
                                                    disabled={isOperationInProgress}
                                                >
                                                    Mostrar elemento <i className="bi bi-eye"></i>
                                                </button>
                                            )}
                                        </div>

                                        {/* Mobile Bottom Navigation Buttons */}
                                        <div className="Riddle__Quiz__BottomSection d-md-none mt-3">
                                            {currentIndex > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={handlePrev}
                                                    className="Btn__Adsl--RedOutlineTransparent Riddle__Quiz__BottomSection__Button"
                                                    disabled={isOperationInProgress}
                                                >
                                                    <i className="bi bi-caret-left-fill"></i>
                                                </button>
                                            )}
                                            {isItemVisible ? (
                                                currentIndex < quizz.length - 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={handleNext}
                                                        className="Btn__Adsl--RedOutlineTransparent Riddle__Quiz__BottomSection__Button"
                                                        disabled={isOperationInProgress}
                                                    >
                                                        <i className="bi bi-caret-right-fill"></i>
                                                    </button>
                                                )
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => handleShowItemOnScreen(currentQuestion)}
                                                    className="Btn__Adsl--RedOutlineTransparent Riddle__Quiz__RightSection__Button"
                                                    disabled={isOperationInProgress}
                                                >
                                                    <i className="bi bi-eye"></i> Revelar
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* 5. CONTROLES DE JUEGO EN VIVO DEL ADMINISTRADOR */}
                                <div className="card border-0 shadow-sm rounded-4 p-4 bg-white">
                                    <h5 className="fw-bold text-center text-dark mb-3">
                                        <i className="bi bi-sliders2-vertical text-danger me-2"></i>
                                        Controles del Operador en Vivo
                                    </h5>

                                    <div className="d-flex flex-wrap gap-3 justify-content-center align-items-center">
                                        {!gameStarted ? (
                                            <button
                                                type="button"
                                                className="btn btn-primary btn-lg fw-bold px-5 shadow-sm"
                                                onClick={StartGame}
                                                disabled={isOperationInProgress}
                                            >
                                                <i className="bi bi-play-circle-fill me-2"></i> Iniciar Juego
                                            </button>
                                        ) : (
                                            <>
                                                {!isItemVisible && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-warning btn-lg fw-bold px-4 shadow-sm text-dark"
                                                        onClick={() => handleShowItemOnScreen(currentQuestion)}
                                                        disabled={isOperationInProgress}
                                                    >
                                                        <i className="bi bi-eye-fill me-2"></i> Revelar Silueta / Oculto
                                                    </button>
                                                )}

                                                {currentIndex < quizz.length - 1 ? (
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary btn-lg fw-bold px-4 shadow-sm"
                                                        onClick={handleNext}
                                                        disabled={isOperationInProgress}
                                                    >
                                                        Siguiente Adivinanza <i className="bi bi-arrow-right-circle-fill ms-2"></i>
                                                    </button>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="btn btn-success btn-lg fw-bold px-4 shadow-sm"
                                                        onClick={ShowResultsView}
                                                        disabled={isOperationInProgress}
                                                    >
                                                        <i className="bi bi-trophy-fill me-2"></i> Mostrar Resultados en Pantalla
                                                    </button>
                                                )}

                                                <button
                                                    type="button"
                                                    className="btn btn-outline-info fw-bold px-4"
                                                    onClick={ShowResultsView}
                                                    disabled={isOperationInProgress}
                                                >
                                                    <i className="bi bi-bar-chart-fill me-2"></i> Ver Métricas
                                                </button>

                                                <button
                                                    type="button"
                                                    className="btn btn-outline-danger fw-bold px-4"
                                                    onClick={handlePromptResetResults}
                                                    disabled={isOperationInProgress}
                                                >
                                                    <i className="bi bi-arrow-counterclockwise me-1"></i> Reiniciar
                                                </button>
                                            </>
                                        )}
                                    </div>

                                    {errorMessage && (
                                        <p className="text-danger mb-0 text-center small mt-3">
                                            <i className="bi bi-exclamation-circle me-1"></i>
                                            {errorMessage}
                                        </p>
                                    )}
                                </div>
                            </>
                        )}
                    </>
                )}
            </section>

            {/* MODALS */}
            {isTriviaModalOpen && (
                <RiddleTriviaModal
                    isOpen={isTriviaModalOpen}
                    onClose={() => setIsTriviaModalOpen(false)}
                    currentActiveTriviaId={selectedTriviaSetId}
                    onActiveTriviaChanged={async (newId) => {
                        await handleTriviaSetSelectionChange(newId);
                        const setsRes = await getTriviaSets();
                        if (setsRes.success) setTriviaSetsList(setsRes.data);
                    }}
                />
            )}

            {isThemeModalOpen && (
                <RiddleThemeModal
                    isOpen={isThemeModalOpen}
                    onClose={() => setIsThemeModalOpen(false)}
                    onSettingsSaved={(newSettings) => {
                        if (newSettings.screenLayout) setCurrentScreenLayout(newSettings.screenLayout);
                        if (newSettings.showCorrectAnswer !== undefined) setShowCorrectAnswer(newSettings.showCorrectAnswer);
                    }}
                />
            )}

            {isQrModalOpen && (
                <RiddleQrModal
                    isOpen={isQrModalOpen}
                    onClose={() => setIsQrModalOpen(false)}
                />
            )}

            {isConfirmModalOpen && (
                <Confirm
                    message={confirmConfig.message}
                    actionToYesButton={confirmConfig.actionToYesButton}
                    textYesButton={confirmConfig.textYesButton}
                    textNoButton={confirmConfig.textNoButton}
                    setShowModal={setIsConfirmModalOpen}
                    setConfirmData={() => {}}
                />
            )}
        </SecondaryBase>
    );
};

export default Riddle;